"use client";

import React, { useEffect, useMemo, useState, useRef } from "react";
import AgChartRenderer, { AgChartRendererHandle } from "./AgChartRenderer";
import type { AgChartOptions, SeriesOptions } from "ag-charts-community";
import useOsDark from "../../hooks/useOsDark";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from "@os/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@os/components/ui/select";
import { Checkbox } from "@os/components/ui/checkbox";
import { Button } from "@os/components/ui/button";
import { Input } from "@os/components/ui/input";
import { Pencil } from "lucide-react";

type TableData = {
  headers: string[];
  rows: Record<string, string | number>[];
};

type ChartConfig = {
  chartType: ChartType;
  xKey: string;
  selectedSeries: string[];
  stacking: boolean;
  legend: boolean;
  title: string;
};

type ChartModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  table: TableData | null;
  sheetName?: string;
  onCreateChart?: (payload: { imageSrc: string; config: ChartConfig }) => void;
};

type ChartType = "bar" | "line" | "area" | "scatter" | "pie";

const MapleSheetChartModal: React.FC<ChartModalProps> = ({ open, onOpenChange, table, sheetName, onCreateChart }) => {
  const [chartType, setChartType] = useState<ChartType>("bar");
  const [xKey, setXKey] = useState<string>("");
  const [selectedSeries, setSelectedSeries] = useState<string[]>([]);
  const [stacking, setStacking] = useState<boolean>(true);
  const [legend, setLegend] = useState<boolean>(true);
  const [chartTitle, setChartTitle] = useState<string>("");
  const [editingTitle, setEditingTitle] = useState<boolean>(false);
  const chartRef = useRef<AgChartRendererHandle | null>(null);

  // Respect MapleOS desktop theme and react to changes
  const isDark = useOsDark();

  // Initialize defaults when table changes
  useEffect(() => {
    if (!table || table.headers.length === 0) return;
    const headers = table.headers;

    // Prefer first header as xKey
    let initialX = headers[0] ?? "";
    setXKey(initialX);

    // Detect numeric headers based on data sample
    const numericHeaders = headers.filter((h) =>
      (table.rows || []).some((r) => typeof r[h] === "number")
    );

    // Default series: first 1-3 numeric columns excluding xKey
    const defaultSeries = numericHeaders.filter((h) => h !== initialX).slice(0, 3);
    setSelectedSeries(defaultSeries);

    // Default title
    setChartTitle(sheetName ? `${sheetName} Chart` : "Sheet Chart");
  }, [table, sheetName]);

  const toggleSeries = (key: string) => {
    setSelectedSeries((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const options: AgChartOptions = useMemo(() => {
    const data = table?.rows ?? [];
    const headers = table?.headers ?? [];

    // Build series array based on type
    let series: SeriesOptions[] = [];

    if (chartType === "pie") {
      const yCol = selectedSeries[0];
      if (yCol && xKey) {
        series = [
          {
            type: "pie",
            angleKey: yCol,
            labelKey: xKey,
            title: yCol,
          } as SeriesOptions,
        ];
      }
    } else {
      series = selectedSeries.map((col) => {
        const common = {
          xKey,
          yKey: col,
          yName: col,
        };
        if (chartType === "bar") {
          return { type: "bar", stacked: stacking, ...common } as SeriesOptions;
        }
        if (chartType === "area") {
          return { type: "area", stacked: stacking, ...common } as SeriesOptions;
        }
        if (chartType === "line") {
          return { type: "line", ...common } as SeriesOptions;
        }
        // scatter requires numeric xKey; we'll still render with provided xKey
        return { type: "scatter", ...common } as SeriesOptions;
      });
    }

    return {
      theme: isDark ? "ag-default-dark" : "ag-default",
      title: { text: editingTitle ? "" : chartTitle },
      autoSize: true,
      legend: { enabled: legend },
      data,
      series,
      axes:
        chartType === "pie"
          ? undefined
          : [
              { type: "category", position: "bottom" },
              { type: "number", position: "left" },
            ],
    } as AgChartOptions;
  }, [table, chartType, xKey, selectedSeries, stacking, legend, isDark, chartTitle, editingTitle]);

  const hasEnoughData =
    !!table &&
    table.headers.length >= 2 &&
    (chartType === "pie" ? selectedSeries.length >= 1 && !!xKey : selectedSeries.length >= 1 && !!xKey);

  const handleCreate = () => {
    const dataUrl = chartRef.current?.getImageDataURL();
    if (!dataUrl) return;
    onCreateChart?.({
      imageSrc: dataUrl,
      config: {
        chartType,
        xKey,
        selectedSeries,
        stacking,
        legend,
        title: chartTitle,
      },
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-4xl ${isDark ? 'dark' : ''}`}>
        <DialogHeader>
          <DialogTitle>Create Chart</DialogTitle>
          <DialogDescription>
            Select the chart type, title, X axis, and one or more series to visualize your sheet data.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Controls */}
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Chart type</label>
              <Select value={chartType} onValueChange={(v) => setChartType(v as ChartType)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="line">Line</SelectItem>
                  <SelectItem value="area">Area</SelectItem>
                  <SelectItem value="scatter">Scatter</SelectItem>
                  <SelectItem value="pie">Pie</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Title</label>
              <Input
                value={chartTitle}
                onChange={(e) => setChartTitle(e.target.value)}
                placeholder="Chart title"
              />
              <p className="text-xs text-muted-foreground">You can also edit the title directly on the preview.</p>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">X axis</label>
              <Select value={xKey} onValueChange={(v) => setXKey(v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select X" />
                </SelectTrigger>
                <SelectContent>
                  {(table?.headers ?? []).map((h) => (
                    <SelectItem key={h} value={h}>
                      {h}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Series</label>
              <div className="max-h-40 overflow-auto rounded border p-2">
                {(table?.headers ?? [])
                  .filter((h) => h !== xKey)
                  .map((h) => {
                    const isNumeric = (table?.rows ?? []).some((r) => typeof r[h] === "number");
                    return (
                      <label key={h} className="flex items-center gap-2 py-1 text-sm">
                        <Checkbox
                          checked={selectedSeries.includes(h)}
                          onCheckedChange={() => toggleSeries(h)}
                        />
                        <span className="flex-1">{h}</span>
                        <span className="text-xs text-muted-foreground">{isNumeric ? "123" : "abc"}</span>
                      </label>
                    );
                  })}
              </div>
              {chartType === "pie" && selectedSeries.length > 1 && (
                <p className="text-xs text-muted-foreground">
                  Pie charts use a single series. Only the first selected series will be used.
                </p>
              )}
            </div>

            {(chartType === "bar" || chartType === "area") && (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={stacking} onCheckedChange={() => setStacking((s) => !s)} />
                <span>Stack series</span>
              </label>
            )}

            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={legend} onCheckedChange={() => setLegend((l) => !l)} />
              <span>Show legend</span>
            </label>

            {chartType === "scatter" && (
              <p className="text-xs text-muted-foreground">
                Scatter charts work best when the X axis is numeric.
              </p>
            )}
          </div>

          {/* Preview */}
          <div className="md:col-span-2 rounded border bg-card relative">
            {/* Inline title editor overlay - moved to top-right, clearer styling */}
            <div className="absolute top-2 right-2 z-[10001]">
              {editingTitle ? (
                <Input
                  autoFocus
                  value={chartTitle}
                  onChange={(e) => setChartTitle(e.target.value)}
                  onBlur={() => setEditingTitle(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setEditingTitle(false);
                  }}
                  className="w-72 text-sm bg-background border border-border ring-1 ring-border shadow-sm"
                />
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setEditingTitle(true)}
                  className="bg-background/90 text-foreground border border-border shadow-sm hover:bg-background"
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit title
                </Button>
              )}
            </div>

            {hasEnoughData ? (
              <div className="h-[320px] w-full">
                <AgChartRenderer ref={chartRef} options={options} />
              </div>
            ) : (
              <div className="p-4 text-sm text-muted-foreground">
                Not enough data. Add headers in the first row and at least one data row with values.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={handleCreate} disabled={!hasEnoughData}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MapleSheetChartModal;